import * as React from 'react';
import {
  IMAGE_DISPLAY_WIDTH,
  catalogImageSrcSet,
  catalogImageVariantUrl,
  isCatalogImageUrl,
  stockxImageSrcSet,
} from '@od-rwa/core/catalog';
import { cn } from '../../../utils/cn';
import { FallbackMark } from './internal/fallback-mark';

export interface ImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'width'
> {
  /**
   * `image` unless given; the fallback takes it with `-fallback` after it.
   */
  'data-testid'?: string;

  /**
   * Above the fold: loads at once (`eager`) and ahead of the page's other
   * pictures (`fetchpriority="high"`).
   */
  isPriority?: boolean;

  /**
   * The slot's CSS fixes the box's height (a height, an aspect ratio), so a
   * lazy picture takes `sizes="auto"`. Never on a box that grows with the
   * picture: an `auto` img is laid out as an empty 300x150 box, so it would
   * draw 150px tall.
   */
  isSizedSlot?: boolean;

  /**
   * A slot whose width changes with the layout, as a `sizes` list taken as it
   * is; wins over `width`.
   */
  sizes?: string;

  /**
   * Null, undefined or empty when there is no picture: the fallback mark shows
   * instead.
   */
  src: string | null | undefined;

  /**
   * WebP variant srcset (e.g. `"thumb.webp 480w, display.webp 1200w"`) served
   * from a `<source>`; without it or `srcSet`, a catalogue picture
   * (`isCatalogImageUrl`) is offered every one of our widths, with the display
   * variant as its src, and a StockX picture each of those widths from StockX,
   * with its link as the src.
   */
  webpSrcSet?: string | null;

  /**
   * CSS pixels, set on the img too: the `sizes` outright, or the fallback for a
   * browser without `auto` in a sized slot. With `height` it sizes the slot.
   */
  width?: number;
}

// Pixels: the slot of a picture given neither width nor sizes, a card's, so 1x takes the 320 file
// and 2x the 640; 100vw takes the 1200 on a phone.
const DEFAULT_SLOT_WIDTH = 320;

/**
 * How far a src got: each error drops one rung, the variants, then the src as
 * given, then the fallback mark.
 */
interface Attempt {
  /**
   * Errors this src has had.
   */
  errors: number;

  /**
   * Set by the load event, so the picture fades in.
   */
  isLoaded: boolean;

  /**
   * The src the attempt is for; a new src starts over.
   */
  src: string | null | undefined;
}

/**
 * A picture that is never broken: it offers the browser every variant to pick
 * from by width and pixel ratio, loads lazily unless it is a priority, fades in
 * on load, and shows the fallback mark when it has no src or the src fails. A
 * lazy picture is sized by its laid-out width (`auto`) only in a sized slot.
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      alt = '',
      className,
      'data-testid': testId = 'image',
      decoding = 'async',
      draggable = false,
      fetchPriority,
      height,
      isPriority = false,
      isSizedSlot = false,
      loading = 'lazy',
      sizes,
      src,
      srcSet,
      style,
      webpSrcSet,
      width,
      onError,
      onLoad,
      ...props
    },
    ref,
  ) => {
    const [attempt, setAttempt] = React.useState<Attempt>({ errors: 0, isLoaded: false, src });
    const current = attempt.src === src ? attempt : { errors: 0, isLoaded: false, src };
    const catalogSrc = !srcSet && src && isCatalogImageUrl(src) ? src : null;
    const stockxSrcSet = !srcSet && !catalogSrc && src ? stockxImageSrcSet(src) : null;
    const variants = srcSet ?? (catalogSrc ? catalogImageSrcSet(catalogSrc, null) : stockxSrcSet);
    const hasVariants = Boolean(webpSrcSet || variants);
    const errorsToFallback = hasVariants ? 2 : 1;
    const isFallback = !src || current.errors >= errorsToFallback;
    const isVariantsShown = current.errors === 0;
    const sourceSrcSet = isVariantsShown ? webpSrcSet : null;
    const imgSrcSet = isVariantsShown && !webpSrcSet ? (variants ?? undefined) : undefined;
    const imgLoading = isPriority ? 'eager' : loading;
    const imgFetchPriority = isPriority ? 'high' : fetchPriority;
    const imgState = current.isLoaded ? 'loaded' : 'loading';
    const imgSrc =
      catalogSrc && imgSrcSet
        ? catalogImageVariantUrl(catalogSrc, IMAGE_DISPLAY_WIDTH)
        : (src ?? undefined);
    const hasShownVariants = Boolean(imgSrcSet || sourceSrcSet);
    const slotSizes = `${width ?? DEFAULT_SLOT_WIDTH}px`;
    const isAutoSized = imgLoading === 'lazy' && (isSizedSlot || Boolean(width && height));
    const derivedSizes = isAutoSized ? `auto, ${slotSizes}` : slotSizes;
    const variantSizes = hasShownVariants ? derivedSizes : undefined;
    const imgSizes = sizes ?? variantSizes;
    const aspectRatio = width && height ? `${width} / ${height}` : undefined;
    const fallbackStyle = { aspectRatio, ...style };
    const fallbackLabel = alt || undefined;
    const fallbackRole = alt ? 'img' : undefined;
    const isFallbackHidden = alt ? undefined : true;
    const fallbackTestId = `${testId}-fallback`;

    const classes = {
      fallback: cn('bg-surface-1 text-fg-3 grid aspect-square place-items-center', className),
      img: cn('transition-opacity duration-300 data-[state=loading]:opacity-0', className),
    };

    const handleError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        const errors = current.errors + 1;
        setAttempt({ errors, isLoaded: false, src });
        if (errors >= errorsToFallback) onError?.(event);
      },
      [current.errors, errorsToFallback, onError, src],
    );

    const handleLoad = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        setAttempt({ errors: current.errors, isLoaded: true, src });
        onLoad?.(event);
      },
      [current.errors, onLoad, src],
    );

    if (isFallback)
      return (
        <span
          aria-hidden={isFallbackHidden}
          aria-label={fallbackLabel}
          className={classes.fallback}
          data-state="fallback"
          data-testid={fallbackTestId}
          role={fallbackRole}
          style={fallbackStyle}
        >
          <FallbackMark />
        </span>
      );

    const img = (
      <img
        ref={ref}
        alt={alt}
        className={classes.img}
        data-state={imgState}
        data-testid={testId}
        decoding={decoding}
        draggable={draggable}
        fetchPriority={imgFetchPriority}
        height={height}
        loading={imgLoading}
        sizes={imgSizes}
        src={imgSrc}
        srcSet={imgSrcSet}
        style={style}
        width={width}
        {...props}
        onError={handleError}
        onLoad={handleLoad}
      />
    );

    if (!sourceSrcSet) return img;

    /**
     * `contents`: an `items-center` flex frame would size `<picture>` to its
     * content and letterbox the img.
     */
    return (
      <picture className="contents">
        <source
          sizes={imgSizes}
          srcSet={sourceSrcSet}
          type="image/webp"
        />
        {img}
      </picture>
    );
  },
);
Image.displayName = 'Image';
